import React from 'react';
import { Table, TableRow, TableHead, TableCell, TableBody, TableContainer, IconButton, TableSortLabel } from '@mui/material';
import { Item } from '../../../../app/services/api/generated';
import CustomPagination from '../../../../components/forms/Pagination';
import { Trash01 } from '../../../../assets';
import { theme } from '../../../../theme';
import { useTranslation } from 'react-i18next';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { Pill } from '../../../../components/shared/Pill';
import { TableLoading } from '../../../../components/forms/TableLoading';
import { EmptyRows } from '../../../../components/forms/EmptyRows';
import { formatDateString } from '../../../../utils';
import { UserContext } from '../../../../components/shared/useUser';

const HeaderTableCell = (props: { dataCol: keyof Item, colDisplay: string, sort: boolean, fieldToSort: string | undefined, direction: 'desc' | 'asc' | undefined, handleSort: (a: keyof Item) => void, align?: 'center' | 'left' }) => {
  return (
    <TableCell
      align={props.align ?? 'left'}
    >
      {props.sort ? <TableSortLabel
        active={props.fieldToSort === props.dataCol}
        direction={props.direction}
        onClick={() => props.handleSort(props.dataCol)}
      >
        {props.colDisplay}
      </TableSortLabel>
        :
        <>{props.colDisplay}</>}
    </TableCell>
  );
};

export const DoNotCountItemsTable = (props: { items: Item[], page: number, setPage: React.Dispatch<React.SetStateAction<number>>, maxPages: number, handleOnDelete: (itemNumber: string) => void, sortDirection: 'desc' | 'asc' | undefined, fieldToSort: string | undefined, handleSort: (property: keyof Item) => void, isLoading: boolean, searchTerm: string, handleConfirmModal: () => void, handleClearButtonClick: () => void, totalCount: number }) => {
  const { t } = useTranslation('doNotCountItems');
  const { selectedStore } = React.useContext(UserContext);

  if (props.items.length == 0 && !props.isLoading) {
    return <EmptyTable bodyRowHeight={40}
      headerHeight={45}
      text={t('title')}
      searchTerm={props.searchTerm}
      onClear={() => { props.handleClearButtonClick(); }}
      onAdd={() => props.handleConfirmModal()}
      onAddIsCreating={false}
      addItemText={t('addItem')}
      onAddIsDisabled={false} />;
  }

  return <>
    <TableHeader>
      {t('title')}
      {props.totalCount > 0 && <Pill text={`${props.totalCount} ${t('items')}`}></Pill>}
    </TableHeader>
    {props.isLoading ? <TableLoading headerHeight={45}
      bodyRowHeight={40}
      minTableWidth={950} /> :
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow >
              <HeaderTableCell dataCol='itemNumber'
                colDisplay={t('itemNumber')}
                sort={true}
                handleSort={props.handleSort}
                direction={props.sortDirection}
                fieldToSort={props.fieldToSort}
              />
              <HeaderTableCell dataCol='description'
                colDisplay={t('name')}
                sort={true}
                handleSort={props.handleSort}
                direction={props.sortDirection}
                fieldToSort={props.fieldToSort}
              />
              <HeaderTableCell dataCol='uom'
                colDisplay={t('uom')}
                sort={true}
                handleSort={props.handleSort}
                direction={props.sortDirection}
                fieldToSort={props.fieldToSort}
              />
              <HeaderTableCell dataCol='primaryCategory'
                colDisplay={t('category')}
                sort={true}
                handleSort={props.handleSort}
                direction={props.sortDirection}
                fieldToSort={props.fieldToSort}
              />
              <HeaderTableCell dataCol='doNotCountCreatedDate'
                colDisplay={t('doNotCountCreatedDate')}
                sort={true}
                handleSort={props.handleSort}
                direction={props.sortDirection}
                fieldToSort={props.fieldToSort}
              />
              <HeaderTableCell dataCol='id'
                colDisplay={t('remove')}
                sort={false}
                handleSort={props.handleSort}
                direction={props.sortDirection}
                fieldToSort={props.fieldToSort}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {props.items.map(item => (<TableRow key={item.itemNumber}>
              <TableCell>{item.itemNumber}</TableCell>
              <TableCell>{item.description}</TableCell>
              <TableCell>{item.uom}</TableCell>
              <TableCell>{item.primaryCategory}</TableCell>
              <TableCell>{item.doNotCountCreatedDate ? formatDateString(item.doNotCountCreatedDate, selectedStore?.franchiseName) : ''}</TableCell>
              <TableCell sx={{ p: 0 }}>
                <IconButton size='small'
                  onClick={() => props.handleOnDelete(item.itemNumber ?? '-1')}>
                  <Trash01
                    color={theme.palette.error[500]}
                  />
                </IconButton>
              </TableCell>
            </TableRow>
            ))}
            <EmptyRows numberOfCurrentRows={props.items.length ?? 0}
              height={40} />
          </TableBody>
        </Table>
      </TableContainer>
    }
    <CustomPagination
      page={props.page}
      setPage={props.setPage}
      maxPages={props.maxPages}
      useNoDataMessage={false}
    />
  </>;
};